import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { AgentsAiProvider } from '@trovata/app/features/ai-agents/services/agents-ai-provider';
import { CashAgentsAiProvider } from '@trovata/app/features/ai-agents/services/cash-agents-ai.provider';
import { TmsAgentsAiProvider } from '@trovata/app/features/ai-agents/services/tms-agents-ai.provider';
import { AgentStreamEvent } from '@trovata/app/features/ai-agents/models/agents-websocket.model';
import { AgentsState } from '@trovata/app/features/ai-agents/store/state/agents.state';

/**
 * AgentsAiProviderRouter — the app-wide runtime-routing edit-AI (WebSocket) provider.
 *
 * `AgentsState` is a root singleton (`provideStore(TROVATA_APP_STATES,…)`), so a
 * `/tms` route-level provider override would never reach it. Instead, the root
 * `AGENTS_AI_PROVIDER` token resolves to THIS router, which dispatches each call
 * to the right WS backend at runtime:
 *
 * - Active context is TMS (`/tms/*` route) → `TmsAgentsAiProvider`
 *   (TMS-scoped `ai.tms.agent.chat.configuration.v1` / `…preview.v1`).
 * - Otherwise (Cash, the default) → `CashAgentsAiProvider`, a transparent
 *   pass-through to the existing `AgentsWebSocketService`.
 *
 * Cash isolation: Cash & TMS are mutually-exclusive sibling routes. On any
 * non-`/tms` URL this router is a pure pass-through to the unchanged Cash
 * provider, so the Cash builder path is provably unaffected — the only added
 * surface is one runtime branch that defaults to Cash. Mirrors the
 * `AgentsDataProviderRouter` REST seam.
 *
 * Spec lock: `documentation/implementation/system/tms-agents-migration.md`
 * §4 ("DI correction") + §5 P5.
 */
@Injectable({ providedIn: 'root' })
export class AgentsAiProviderRouter implements AgentsAiProvider {
	private readonly router: Router = inject(Router);
	private readonly store: Store = inject(Store);
	private readonly cashProvider: CashAgentsAiProvider = inject(CashAgentsAiProvider);
	private readonly tmsProvider: TmsAgentsAiProvider = inject(TmsAgentsAiProvider);

	/** Route prefix that identifies the TMS product app. */
	private static readonly TMS_ROUTE_PREFIX: string = '/tms';

	sendConfigurationChat(prompt: string, sessionId?: string): Observable<AgentStreamEvent> {
		return this.active().sendConfigurationChat(prompt, sessionId);
	}

	sendPreviewChat(prompt: string, timezone?: string): Observable<AgentStreamEvent> {
		return this.active().sendPreviewChat(prompt, timezone);
	}

	abortChatStream(): void {
		this.active().abortChatStream();
	}

	abortPreviewStream(): void {
		this.active().abortPreviewStream();
	}

	abortStream(): void {
		this.active().abortStream();
	}

	/**
	 * Resolves the provider for the current context. Prefers the EXPLICIT context declared by the active
	 * surface (`AgentsState.explicitContext`, set by the panel from its `context` input) — the reliable
	 * SSOT — over the `router.url` inference, which races on a hard refresh / SPA nav into `/tms` because
	 * routed components mount before `NavigationEnd` settles the URL. Falls back to `router.url` (then
	 * Cash) when no surface has declared a context. Mirrors `AgentsDataProviderRouter.active()`.
	 */
	private active(): AgentsAiProvider {
		const explicit: 'cash' | 'tms' | null = this.store.selectSnapshot(AgentsState.explicitContext);
		const isTms: boolean = explicit !== null ? explicit === 'tms' : this.router.url.startsWith(AgentsAiProviderRouter.TMS_ROUTE_PREFIX);
		return isTms ? this.tmsProvider : this.cashProvider;
	}
}
